'use client';

import React from 'react';
import Link from 'next/link';
import type { RestaurantRecord } from '@bip/api-client';
import { buildRestaurantUrl, RESOS_CDN_BASE, RESTAURANT_NO_IMAGE_URL } from '../config';

interface RestaurantCardProps {
  restaurant: RestaurantRecord;
  /** Booking date — YYYY-MM-DD. Forwarded to the detail-page URL when set. */
  date?: string | null;
  /** Slot time — HH:mm. Forwarded to the detail-page URL when set. */
  time?: string | null;
  /** Guest count / party size. Forwarded to the detail-page URL when set. */
  guests?: number | null;
  people?: number | null;
}

export function RestaurantCard({
  restaurant,
  date,
  time,
  guests,
  people,
}: RestaurantCardProps) {
  const detailUrl = buildRestaurantUrl(
    restaurant.countrySlug,
    restaurant.stateSlug,
    restaurant.citySlug,
    restaurant.slug,
    restaurant.id,
    date,
    guests ?? people,
    time,
  );

  // Address line per spec: Address1, State, City, Country — skipping any
  // missing parts so we don't emit trailing/leading commas. The reference
  // joins the available parts with a simple `, ` delimiter.
  // Format matching the spec: "address1, state, city, country", using only
  // the parts this restaurant actually has.
  const locationText = [
    restaurant.address?.address1,
    restaurant.address?.state,
    restaurant.address?.city,
    restaurant.address?.country,
  ].filter(Boolean).join(', ');

  // resOS's own `image` field is only populated when the venue set a
  // primary photo through resOS itself. Venues onboarded solely through
  // the BookItPlease add-on carry their photo(s) under
  // `addOns.bookItPlease.images[]` instead (same fallback the checkout/
  // table-selection/confirmation pages already apply) — fall back to the
  // first one so the listing card isn't stuck on the placeholder.
  const bipImages = (restaurant.addOns as { bookItPlease?: { images?: { image: string }[] } } | undefined)
    ?.bookItPlease?.images;
  const primaryImagePath = restaurant.image || bipImages?.[0]?.image || undefined;

  const imageUrl = primaryImagePath
    ? primaryImagePath.startsWith('http')
      ? primaryImagePath
      : `${RESOS_CDN_BASE}${primaryImagePath}`
    : RESTAURANT_NO_IMAGE_URL;

  return (
    <div className="mb-3">
      <div className="Recommended-bx cutout-shape overflow-hidden">
        <div className="cutout-inner">
          <Link href={detailUrl} className="block">
            <img
              src={imageUrl}
              alt={`Dining room at ${restaurant.name}`}
              className="h-[180px] w-full object-contain bg-gray-100"
              onError={(e) => { (e.target as HTMLImageElement).src = RESTAURANT_NO_IMAGE_URL; }}
            />
          </Link>
          <div className="p-3">
            {/* <div className="card-display justify-content-between">
              <div className="pb-3">
                <div className="category-text">{categoryText}</div>
              </div>
            </div> */}

            <div className="heading">
              <h4>
                <Link href={detailUrl} className="text-black">
                  {restaurant.name}
                </Link>
              </h4>
            </div>

            {locationText && (
              <div className="pb-2 sports-text">
              <i className="text-lg fal fa-map-marker-alt pr-2"></i>
                {locationText}
              </div>
            )}

            <div className="pt-2 d-flex align-items-center justify-content-between Recommended-web-pad">
              <Link href={detailUrl}>
                <span className="btn btn-primary btn-sm getPropertyDetail list-book-now-btn">
                  Book Table
                </span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
